<template>
  <div
    class="clinical-file-upload"
    :class="{
      'is-dragging': dragging,
      'has-files': previews.length,
      'is-disabled': disabled,
    }"
    @dragenter.prevent="setDragging(true)"
    @dragover.prevent="setDragging(true)"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="input"
      class="clinical-file-upload__input"
      type="file"
      v-bind="$attrs"
      :accept="accept"
      :capture="capture"
      :disabled="disabled"
      :multiple="multiple"
      :required="required"
      :aria-label="ariaLabel"
      @change="onInputChange"
    />

    <div class="clinical-file-upload__prompt">
      <span class="clinical-file-upload__icon" aria-hidden="true">
        <i class="fas fa-cloud-upload-alt"></i>
      </span>
      <span class="clinical-file-upload__copy">
        <strong>{{ titleText }}</strong>
        <small>{{ descriptionText }}</small>
      </span>
      <span class="clinical-file-upload__button">{{ browseText }}</span>
    </div>

    <div v-if="previews.length" class="clinical-file-upload__previews" aria-live="polite">
      <article v-for="file in previews" :key="file.id" class="clinical-file-preview">
        <img
          v-if="file.previewUrl"
          :src="file.previewUrl"
          :alt="`Preview ${file.name}`"
          class="clinical-file-preview__image"
        />
        <span
          v-else
          class="clinical-file-preview__type"
          :class="file.typeClass"
          aria-hidden="true"
        >
          <i :class="file.iconClass"></i>
        </span>
        <span class="clinical-file-preview__meta">
          <strong :title="file.name">{{ file.name }}</strong>
          <small>{{ file.typeLabel }} · {{ file.sizeLabel }}</small>
        </span>
      </article>
    </div>
  </div>
</template>

<script>
export default {
  name: "ClinicalFileUpload",
  inheritAttrs: false,
  props: {
    value: { default: null },
    multiple: { type: Boolean, default: false },
    accept: { type: String, default: "" },
    capture: { type: [String, Boolean], default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    browseText: { type: String, default: "Pilih file" },
    placeholder: { type: String, default: "Belum ada file dipilih" },
    dropPlaceholder: { type: String, default: "Lepaskan file di sini" },
    hint: { type: String, default: "" },
    ariaLabel: { type: String, default: "Pilih atau tarik file" },
  },
  data() {
    return {
      dragging: false,
      selectedFiles: [],
      previews: [],
    };
  },
  computed: {
    titleText() {
      if (this.dragging) return this.dropPlaceholder;
      if (!this.selectedFiles.length) {
        return this.multiple
          ? "Tarik & lepas file ke area ini"
          : "Tarik & lepas file ke area ini";
      }
      return this.multiple
        ? `${this.selectedFiles.length} file siap diunggah`
        : "File siap diunggah";
    },
    descriptionText() {
      if (!this.selectedFiles.length) {
        return this.hint || (this.multiple
          ? "atau klik untuk memilih satu maupun beberapa file"
          : "atau klik untuk memilih satu file");
      }
      return this.selectedFiles.map((file) => file.name).join(", ");
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        const files = Array.isArray(value) ? value : value ? [value] : [];
        const validFiles = files.filter((file) => this.isFile(file));
        if (!validFiles.length) {
          if (!value || (Array.isArray(value) && !value.length)) this.clear(false);
          return;
        }
        if (this.fileSignature(validFiles) !== this.fileSignature(this.selectedFiles)) {
          this.setLocalFiles(validFiles);
        }
      },
    },
  },
  beforeDestroy() {
    this.revokePreviews();
  },
  methods: {
    isFile(value) {
      return typeof File !== "undefined" && value instanceof File;
    },
    fileSignature(files) {
      return files.map((file) => `${file.name}:${file.size}:${file.lastModified}`).join("|");
    },
    setDragging(value) {
      if (!this.disabled) this.dragging = value;
    },
    onDragLeave(event) {
      if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
      this.dragging = false;
    },
    onDrop(event) {
      this.dragging = false;
      if (this.disabled) return;
      const files = Array.from(event.dataTransfer && event.dataTransfer.files || []);
      this.commitFiles(files, { target: { files }, dataTransfer: event.dataTransfer });
    },
    onInputChange(event) {
      this.commitFiles(Array.from(event.target.files || []), event);
    },
    commitFiles(files, changeEvent) {
      const selected = (this.multiple ? files : files.slice(0, 1)).filter(Boolean);
      this.setLocalFiles(selected);
      this.$emit("input", this.multiple ? selected : selected[0] || null);
      this.$emit("change", changeEvent);
      this.$emit("files-change", selected);
    },
    setLocalFiles(files) {
      this.revokePreviews();
      this.selectedFiles = files;
      this.previews = files.map((file, index) => {
        const type = this.fileType(file);
        return {
          id: `${file.name}-${file.size}-${file.lastModified || index}`,
          name: file.name,
          previewUrl: type.key === "image" ? window.URL.createObjectURL(file) : null,
          iconClass: type.iconClass,
          typeClass: `is-${type.key}`,
          typeLabel: type.label,
          sizeLabel: this.fileSize(file.size),
        };
      });
    },
    fileType(file) {
      const mime = (file.type || "").toLowerCase();
      const extension = (file.name.split(".").pop() || "").toLowerCase();
      if (mime.startsWith("image/")) return this.type("image", "Gambar", "fas fa-file-image");
      if (mime === "application/pdf" || extension === "pdf") return this.type("pdf", "PDF", "fas fa-file-pdf");
      if (["doc", "docx", "odt"].includes(extension)) return this.type("word", "Dokumen", "fas fa-file-word");
      if (["xls", "xlsx", "csv", "ods"].includes(extension)) return this.type("excel", "Spreadsheet", "fas fa-file-excel");
      if (["ppt", "pptx", "odp"].includes(extension)) return this.type("powerpoint", "Presentasi", "fas fa-file-powerpoint");
      if (mime.startsWith("video/")) return this.type("video", "Video", "fas fa-file-video");
      if (mime.startsWith("audio/")) return this.type("audio", "Audio", "fas fa-file-audio");
      if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) return this.type("archive", "Arsip", "fas fa-file-archive");
      if (mime.startsWith("text/") || ["txt", "rtf"].includes(extension)) return this.type("text", "Teks", "fas fa-file-alt");
      return this.type("file", extension ? extension.toUpperCase() : "File", "fas fa-file");
    },
    type(key, label, iconClass) {
      return { key, label, iconClass };
    },
    fileSize(size) {
      if (!size) return "0 KB";
      const units = ["B", "KB", "MB", "GB"];
      const unitIndex = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
      const value = size / Math.pow(1024, unitIndex);
      return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
    },
    revokePreviews() {
      this.previews.forEach((file) => {
        if (file.previewUrl) window.URL.revokeObjectURL(file.previewUrl);
      });
      this.previews = [];
    },
    clear(emit = true) {
      this.dragging = false;
      this.revokePreviews();
      this.selectedFiles = [];
      if (this.$refs.input) this.$refs.input.value = "";
      if (emit) {
        this.$emit("input", this.multiple ? [] : null);
        this.$emit("files-change", []);
      }
    },
    reset() {
      this.clear();
    },
    open() {
      if (!this.disabled && this.$refs.input) this.$refs.input.click();
    },
  },
};
</script>

<style scoped>
.clinical-file-upload { position: relative; padding: 16px; border: 2px dashed #98d1cb; border-radius: 14px; background: #f4fbfa; cursor: pointer; transition: border-color 160ms ease, background 160ms ease, box-shadow 160ms ease, transform 160ms ease; }
.clinical-file-upload:hover, .clinical-file-upload:focus-within, .clinical-file-upload.is-dragging { border-color: #16867f; background: #eaf8f6; box-shadow: 0 0 0 4px rgba(22, 134, 127, .12); }
.clinical-file-upload.is-dragging { transform: translateY(-1px); }
.clinical-file-upload.has-files { border-style: solid; }
.clinical-file-upload.is-disabled { cursor: not-allowed; opacity: .66; }
.clinical-file-upload__input { position: absolute; z-index: 2; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.clinical-file-upload__input:disabled { cursor: not-allowed; }
.clinical-file-upload__prompt { display: flex; gap: 14px; align-items: center; }
.clinical-file-upload__icon { display: grid; width: 48px; height: 48px; flex: 0 0 48px; place-items: center; color: #16867f; border-radius: 12px; background: #dff5f1; font-size: 19px; }
.clinical-file-upload__copy { min-width: 0; flex: 1; }
.clinical-file-upload__copy strong, .clinical-file-upload__copy small { display: block; }
.clinical-file-upload__copy strong { margin-bottom: 2px; color: #243b53; font-size: 13px; }
.clinical-file-upload__copy small { overflow: hidden; color: #748397; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.clinical-file-upload__button { display: inline-flex; min-height: 42px; flex: 0 0 auto; align-items: center; justify-content: center; padding: 10px 18px; color: #fff; border-radius: 10px; background: linear-gradient(135deg, #173f70, #16978f); font-weight: 700; line-height: 1.25; pointer-events: none; }
.clinical-file-upload__previews { display: grid; gap: 10px; grid-template-columns: repeat(auto-fill, minmax(210px, 1fr)); margin-top: 14px; padding-top: 14px; border-top: 1px solid #d3e9e6; }
.clinical-file-preview { display: flex; min-width: 0; min-height: 68px; gap: 10px; align-items: center; padding: 8px; border: 1px solid #d8e6ed; border-radius: 11px; background: rgba(255, 255, 255, .92); }
.clinical-file-preview__image, .clinical-file-preview__type { width: 52px; height: 52px; flex: 0 0 52px; border-radius: 9px; }
.clinical-file-preview__image { display: block; object-fit: cover; background: #e8f1f5; }
.clinical-file-preview__type { display: grid; place-items: center; color: #4d657b; background: #edf3f7; font-size: 22px; }
.clinical-file-preview__type.is-pdf { color: #c63f4d; background: #fff0f1; }
.clinical-file-preview__type.is-word { color: #2870b9; background: #edf5ff; }
.clinical-file-preview__type.is-excel { color: #24845a; background: #edf9f3; }
.clinical-file-preview__type.is-powerpoint { color: #c45b30; background: #fff3ed; }
.clinical-file-preview__type.is-video, .clinical-file-preview__type.is-audio { color: #7356b6; background: #f4efff; }
.clinical-file-preview__type.is-archive { color: #9a6a27; background: #fff8e9; }
.clinical-file-preview__meta { display: block; min-width: 0; flex: 1; }
.clinical-file-preview__meta strong, .clinical-file-preview__meta small { display: block; }
.clinical-file-preview__meta strong { overflow: hidden; color: #2f4358; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
.clinical-file-preview__meta small { margin-top: 3px; color: #7a8a9e; font-size: 10px; }
@media (max-width: 575.98px) {
  .clinical-file-upload__prompt { align-items: stretch; flex-direction: column; }
  .clinical-file-upload__icon { align-self: center; }
  .clinical-file-upload__copy { text-align: center; }
  .clinical-file-upload__copy small { white-space: normal; }
  .clinical-file-upload__button { width: 100%; }
  .clinical-file-upload__previews { grid-template-columns: 1fr; }
}
</style>
